import { format12HourTimeLowercase, parseISOWithTimezone } from 'helpers/intl-fns';
import { formatOrderId } from 'helpers/sku';

import { LIFECYCLE_STATUS, MARKED_FULFILLED_TYPE, ORDER_ITEM_CANCELLATION_TYPE } from './SkuEnumHelpers';

export const getOrderItemId = orderItem => orderItem?.core?.itemSummary?.itemId;
export const getOrderItemName = orderItem => orderItem?.core?.itemSummary?.name;
export const isCancelledOrderItem = orderItem => Boolean(orderItem?.core?.cancelledAt);

const getModifierName = orderModifier => orderModifier?.modifierSummary?.name;

const aggregateOrderModifiers = orderModifiers => {
  if (!orderModifiers?.length) return [];

  const aggregatedByKey = new Map();
  const orderedKeys = [];

  orderModifiers.forEach(orderModifier => {
    const name = getModifierName(orderModifier);
    if (!name) return;

    const key = orderModifier?.modifierSummary?.modifierId || orderModifier?.orderModifierId || name;
    if (!aggregatedByKey.has(key)) {
      orderedKeys.push(key);
      aggregatedByKey.set(key, {
        name,
        quantity: 0,
        nestedModifierGroups: orderModifier?.nestedModifierGroups || [],
      });
    }

    aggregatedByKey.get(key).quantity += 1;
  });

  return orderedKeys.map(key => aggregatedByKey.get(key));
};

const flattenModifierGroups = (modifierGroups, depth = 0) => {
  if (!modifierGroups?.length) return [];

  return modifierGroups.flatMap(modifierGroup =>
    aggregateOrderModifiers(modifierGroup?.orderModifiers).flatMap(aggregatedModifier => {
      const modifierKey =
        aggregatedModifier.name + (modifierGroup?.orderModifierGroupId ? `-${modifierGroup.orderModifierGroupId}` : '');
      const lines = [
        {
          name: aggregatedModifier.name,
          quantity: aggregatedModifier.quantity,
          depth,
          key: `modifier-${modifierKey}-${depth}`,
        },
      ];

      if (aggregatedModifier.nestedModifierGroups?.length) {
        lines.push(...flattenModifierGroups(aggregatedModifier.nestedModifierGroups, depth + 1));
      }

      return lines;
    })
  );
};

export const formatModifierLinesForFlyout = flattenModifierGroups;

const getModifierFingerprint = modifierGroups => {
  if (!modifierGroups?.length) return '';

  return [...modifierGroups]
    .sort((a, b) => (a.orderModifierGroupId || '').localeCompare(b.orderModifierGroupId || ''))
    .map(group => {
      const sortedModifiers = [...(group.orderModifiers || [])].sort((a, b) =>
        (a.modifierSummary?.modifierId || a.orderModifierId || '').localeCompare(
          b.modifierSummary?.modifierId || b.orderModifierId || ''
        )
      );
      const modParts = sortedModifiers.map(mod => {
        const modId = mod.modifierSummary?.modifierId || mod.orderModifierId || '';
        const nested = getModifierFingerprint(mod.nestedModifierGroups);
        return nested ? `${modId}(${nested})` : modId;
      });
      return `${group.orderModifierGroupId || ''}:[${modParts.join(',')}]`;
    })
    .join('|');
};

const GROUP_KEY_SEPARATOR = '\x1f';

// Groups orderItems and resolves the visible subset for each group.
// - Mixed group (some cancelled, some not): visibleItems = non-cancelled only.
// - Fully-cancelled group: visibleItems = all items (shown, possibly crossed out).
// Entries with no itemId or no resolvable name are excluded.
const buildOrderItemGroups = (orderItems, { groupByModifierConfig = false } = {}) => {
  if (!orderItems?.length) return [];

  const grouped = new Map();
  const orderedKeys = [];

  orderItems.forEach(orderItem => {
    const itemId = getOrderItemId(orderItem);
    if (!itemId) return;
    const fingerprint = groupByModifierConfig ? getModifierFingerprint(orderItem.modifierGroups) : '';
    const key = fingerprint ? `${itemId}${GROUP_KEY_SEPARATOR}${fingerprint}` : itemId;
    if (!grouped.has(key)) {
      orderedKeys.push(key);
      grouped.set(key, []);
    }
    grouped.get(key).push(orderItem);
  });

  return orderedKeys
    .map(key => {
      const group = grouped.get(key);
      const allCancelled = group.every(isCancelledOrderItem);
      const visibleItems = allCancelled ? group : group.filter(item => !isCancelledOrderItem(item));
      const name = getOrderItemName(visibleItems[0]) || null;
      if (!name) return null;
      return { group, allCancelled, visibleItems, name, groupKey: key };
    })
    .filter(Boolean);
};

export const formatOrderTime = (isoDateTime, venueTimezone, locale) => {
  if (!isoDateTime) return null;

  return format12HourTimeLowercase(parseISOWithTimezone(isoDateTime, venueTimezone), locale);
};

export const formatOrderDate = (isoDateTime, venueTimezone, locale) => {
  if (!isoDateTime) return null;

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parseISOWithTimezone(isoDateTime, venueTimezone));
};

export const formatPayableAmount = (money, intl) => {
  if (!money || money.amount === null || money.amount === undefined || !money.currencyCode) {
    return null;
  }

  return intl.formatNumber(money.amount, {
    style: 'currency',
    currency: money.currencyCode,
  });
};

const CP_CANCELLATION_TYPES = new Set([
  ORDER_ITEM_CANCELLATION_TYPE.CP_CANCELLED,
  ORDER_ITEM_CANCELLATION_TYPE.CP_CANCELLED_FULL_PAYOUT,
  ORDER_ITEM_CANCELLATION_TYPE.UNBOOKABLE,
]);

const getLatestCancelledOrderItem = orderItems =>
  orderItems
    ?.filter(item => item?.core?.cancelledAt)
    ?.sort((a, b) => (b?.core?.cancelledAt || '').localeCompare(a?.core?.cancelledAt || ''))[0];

const isUserMissedPickupCancellationType = cancellationType =>
  cancellationType === ORDER_ITEM_CANCELLATION_TYPE.USER_MISSED_PICKUP;

export const isUserMissedPickupOrder = order => {
  if (order?.lifecycleStatus !== LIFECYCLE_STATUS.CANCELLED) return false;

  const latestCancellationType = getLatestCancelledOrderItem(order?.orderItems)?.core?.cancellationType;
  return isUserMissedPickupCancellationType(latestCancellationType);
};

// Builds the display data for each order item group in the flyout.
//   - Non-cancelled items in a mixed group: show non-cancelled only, sum their payable amounts.
//   - All-cancelled group: show with allCancelled=true, payableAmount=$0, basePayableAmount=sum of basePayableAmount.
//   - USER_MISSED_PICKUP: partner still gets paid, so show payout like active/fulfilled orders.
//   - If the latest cancellation in the group is PARTNER_SOLD_OUT, show the
//     out-of-stock label for the cancelled group.
export const formatItemGroupsForFlyout = orderItems =>
  buildOrderItemGroups(orderItems, { groupByModifierConfig: true }).map(
    ({ group, name, allCancelled, visibleItems, groupKey }) => {
      const itemId = getOrderItemId(group[0]);
      const currencyCode = group[0]?.payableAmount?.currencyCode;
      const latestCancelledOrderItem = getLatestCancelledOrderItem(group);
      const isMissedPickup =
        allCancelled && isUserMissedPickupCancellationType(latestCancelledOrderItem?.core?.cancellationType);
      const displayAsCancelled = allCancelled && !isMissedPickup;
      const isOutOfStock =
        allCancelled &&
        latestCancelledOrderItem?.core?.cancellationType === ORDER_ITEM_CANCELLATION_TYPE.PARTNER_SOLD_OUT;
      const payableAmountTotal = displayAsCancelled
        ? 0
        : visibleItems.reduce((sum, item) => sum + (item?.payableAmount?.amount ?? 0), 0);
      const basePayableAmountTotal = displayAsCancelled
        ? group.reduce((sum, item) => sum + (item?.basePayableAmount?.amount ?? 0), 0)
        : null;

      return {
        itemId,
        groupKey,
        name,
        quantity: visibleItems.length,
        allCancelled: displayAsCancelled,
        isOutOfStock,
        payableAmountMoney: { amount: payableAmountTotal, currencyCode },
        basePayableAmountMoney: displayAsCancelled ? { amount: basePayableAmountTotal, currencyCode } : null,
        modifierLines: formatModifierLinesForFlyout(visibleItems[0]?.modifierGroups),
      };
    }
  );

// Formats order items for display in the orders management table.
// Cross out items only in partially-cancelled orders; fully-cancelled orders show status in the status column.
export const formatItemGroupsForTable = (orderItems, lifecycleStatus) =>
  buildOrderItemGroups(orderItems).map(({ group, name, allCancelled, visibleItems, groupKey }) => ({
    itemId: getOrderItemId(group[0]),
    groupKey,
    name,
    count: visibleItems.length,
    isCrossedOut: allCancelled && lifecycleStatus !== LIFECYCLE_STATUS.CANCELLED,
  }));

// Derives the cancellation agent key from the order items.
const getCancellationAgentKey = orderItems => {
  if (!orderItems?.length) return 'partner';
  const latestType = getLatestCancelledOrderItem(orderItems)?.core?.cancellationType;
  return latestType && CP_CANCELLATION_TYPES.has(latestType) ? 'classpass' : 'partner';
};

const getMarkedFulfilledAgentKey = markedFulfilledType =>
  markedFulfilledType === MARKED_FULFILLED_TYPE.AUTO ? 'auto' : 'partner';

// Returns an array of description objects for FULFILLED/CANCELLED lifecycle states.
// Each object has: { action, agentKey, timeDisplay, dateDisplay }
export const formatLifecycleStatusDescriptions = (order, { intl, venueTimezone, locale }) => {
  const lifecycleStatus = order?.lifecycleStatus;
  if (lifecycleStatus !== 'FULFILLED' && lifecycleStatus !== 'CANCELLED') return [];

  const { userMarkedPickupAt, markedFulfilledAt, markedFulfilledType } = order?.core || {};
  const descriptions = [];

  const buildDescription = (action, agentKey, isoInstant) => {
    const timeDisplay = formatOrderTime(isoInstant, venueTimezone, locale);
    const dateDisplay = isoInstant
      ? intl.formatDate(new Date(isoInstant), {
          month: 'long',
          day: 'numeric',
          timeZone: venueTimezone || undefined,
        })
      : null;
    return timeDisplay && dateDisplay ? { action, agentKey, timeDisplay, dateDisplay } : null;
  };

  if (lifecycleStatus === 'FULFILLED') {
    if (userMarkedPickupAt) {
      const desc = buildDescription('marked fulfilled', 'customer', userMarkedPickupAt);
      if (desc) descriptions.push(desc);
    }
    if (markedFulfilledAt) {
      const agentKey = getMarkedFulfilledAgentKey(markedFulfilledType);
      const desc = buildDescription('marked fulfilled', agentKey, markedFulfilledAt);
      if (desc) descriptions.push(desc);
    }
  } else if (lifecycleStatus === 'CANCELLED') {
    const latestCancelledOrderItem = getLatestCancelledOrderItem(order?.orderItems);
    const latestCancelledAt = latestCancelledOrderItem?.core?.cancelledAt;
    if (latestCancelledAt) {
      const agentKey = getCancellationAgentKey(order?.orderItems);
      const action = isUserMissedPickupCancellationType(latestCancelledOrderItem?.core?.cancellationType)
        ? 'marked as missed'
        : 'cancelled';
      const desc = buildDescription(action, agentKey, latestCancelledAt);
      if (desc) descriptions.push(desc);
    }
  }

  return descriptions;
};

export const mapOrderToFlyoutData = (order, { intl, venueTimezone, locale }) => {
  const itemGroups = formatItemGroupsForFlyout(order?.orderItems).map(group => ({
    ...group,
    payableAmountDisplay: formatPayableAmount(group.payableAmountMoney, intl),
    basePayableAmountDisplay: group.basePayableAmountMoney
      ? formatPayableAmount(group.basePayableAmountMoney, intl)
      : null,
  }));

  return {
    orderId: formatOrderId(order?.core?.orderId),
    memberName: order?.userName || null,
    memberInitial: order?.userName?.[0]?.toUpperCase() || null,
    lifecycleStatus: order?.lifecycleStatus,
    isUserMissedPickup: isUserMissedPickupOrder(order),
    itemGroups,
    totalPayableAmountDisplay: formatPayableAmount(order?.payableAmount, intl),
    lifecycleStatusDescriptions: formatLifecycleStatusDescriptions(order, { intl, venueTimezone, locale }),
  };
};
